import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useFan } from "@/lib/fanContext";
import { toast } from "sonner";
import { Building2, MapPin, X, CalendarDays, ArrowUpRight } from "lucide-react";
import { formatDate } from "@/lib/format";

export default function FanFollowingVenues() {
    const { refresh } = useFan();
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        endpoints.fanFollowedVenues().then((d) => {
            setVenues(d.venues);
            setLoading(false);
        }).catch(() => setLoading(false));
    };
    useEffect(load, []);

    const unfollow = async (v) => {
        try {
            await endpoints.fanUnfollowVenue(v.id);
            toast(`Unfollowed ${v.name}`);
            await refresh();
            load();
        } catch {
            toast.error("Unfollow failed");
        }
    };

    return (
        <div className="p-4 md:p-6" data-testid="fan-venues-page">
            <div className="flex items-baseline justify-between border-b border-[#2a2a2a] pb-4 mb-4">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Following</div>
                    <h1 className="mt-1 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Venues</h1>
                </div>
                <div className="text-xs font-mono uppercase text-[#666]" data-testid="fan-venues-count">
                    {loading ? "…" : `${venues.length} venue${venues.length === 1 ? "" : "s"}`}
                </div>
            </div>

            {loading ? (
                <div className="font-mono text-sm text-[#666]" data-testid="fan-venues-loading">Loading…</div>
            ) : venues.length === 0 ? (
                <div className="border border-dashed border-[#2a2a2a] p-10 text-center" data-testid="fan-venues-empty">
                    <Building2 className="w-8 h-8 text-[#666] mx-auto" />
                    <div className="mt-3 text-sm text-[#a3a3a3]">
                        You aren't following any venues yet. Discover rooms across the network.
                    </div>
                    <Link to="/venues" className="pm-btn pm-btn-primary mt-4 inline-flex" data-testid="fan-venues-browse">
                        Browse venues
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                    {venues.map((v) => (
                        <div key={v.id} className="bg-[#0a0a0a] p-4 flex flex-col group" data-testid={`fan-venue-card-${v.id}`}>
                            <Link to={`/venues/${v.id}`} className="relative aspect-[16/10] overflow-hidden block">
                                {v.cover_url || v.image_url ? (
                                    <img src={v.cover_url || v.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                                ) : <div className="absolute inset-0 bg-[#141414]" />}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                <div className="absolute bottom-3 left-3 right-3">
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Venue</div>
                                    <div className="font-display font-black uppercase text-xl leading-tight text-[#f4f4f0] mt-1 truncate">{v.name}</div>
                                </div>
                            </Link>
                            <div className="mt-3 flex items-center gap-2 text-xs text-[#a3a3a3]">
                                <MapPin className="w-3 h-3" /> {v.city}, {v.country}
                            </div>
                            <div className="mt-2 text-[10px] font-mono uppercase text-[#a3a3a3] flex items-center gap-1">
                                <CalendarDays className="w-3 h-3 text-[#ff4400]" />
                                {v.upcoming_events_count || 0} upcoming event{v.upcoming_events_count === 1 ? "" : "s"}
                            </div>
                            {v.next_event && (
                                <Link to={`/events/${v.next_event.id}`} className="mt-2 block border border-[#2a2a2a] p-2 hover:border-[#ff4400]" data-testid={`fan-venue-next-${v.id}`}>
                                    <div className="text-[10px] font-mono uppercase text-[#666]">Next</div>
                                    <div className="text-sm font-display font-bold uppercase truncate">{v.next_event.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">{formatDate(v.next_event.date)} · {v.next_event.artist_name}</div>
                                </Link>
                            )}
                            <div className="mt-3 flex gap-2">
                                <Link to={`/venues/${v.id}`} className="pm-btn text-xs flex-1" data-testid={`fan-venue-view-${v.id}`}>
                                    View venue <ArrowUpRight className="w-3 h-3 ml-1" />
                                </Link>
                                <button onClick={() => unfollow(v)} className="pm-btn text-xs" data-testid={`fan-venue-unfollow-${v.id}`}>
                                    Unfollow
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
